import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Page, PageHeader, Empty } from "@/components/common";
import { CREDIT_HELP } from "@/components/Billing";
import { SuperPrivacyPolicyPanel } from "@/components/SuperPrivacyPolicyPanel";
import { SuperFaqPanel } from "@/components/SuperFaqPanel";
import { SuperLoginScreenPanel } from "@/components/SuperLoginScreenPanel";
import { SuperPageGuidancePanel } from "@/components/SuperPageGuidancePanel";
import { api, errMsg, fmtDate } from "@/lib/api";

const credits = (n) => Number(n || 0).toFixed(2);

function Status({ b }) {
  if (b.locked) return <span className="rounded-full px-2 py-0.5 text-xs bg-red-500/15 text-red-700 dark:text-red-300">Trial ended</span>;
  if (b.plan === "trial") return <span className="rounded-full px-2 py-0.5 text-xs bg-primary/15 text-primary">Trial · {b.days_left}d left</span>;
  return <span className="rounded-full px-2 py-0.5 text-xs bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Subscribed</span>;
}

function Credits({ b }) {
  if (b.credit_limit == null) return <span>{credits(b.credits_used)} AI credits used · <span className="text-muted-foreground">unlimited</span></span>;
  return <span className={b.out_of_credit ? "text-red-600 font-medium" : ""}>{credits(b.credits_used)} / {b.credit_limit} AI credits {b.out_of_credit && "· out"}</span>;
}

function EditDialog({ school, onClose, onSaved }) {
  const b = school.billing;
  const [plan, setPlan] = useState(b.plan);
  const [extend, setExtend] = useState("");
  const [ends, setEnds] = useState(b.trial_ends_at ? b.trial_ends_at.slice(0, 10) : "");
  const [limit, setLimit] = useState(b.credit_limit ?? "");
  const [unlimited, setUnlimited] = useState(b.credit_limit == null);
  const [reset, setReset] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { plan, reset_used: reset, unlimited_credits: unlimited };
      if (!unlimited && limit !== "") body.credit_limit = Number(limit);
      if (extend) body.extend_days = Number(extend);
      else if (ends && ends !== (b.trial_ends_at || "").slice(0, 10)) body.trial_ends_at = `${ends}T23:59:00+00:00`;
      await api.put(`/super/schools/${school.id}`, body);
      toast.success(`${school.name} updated`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="super-edit-dialog">
        <DialogHeader><DialogTitle>{school.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Plan</Label>
            <Select value={plan} onValueChange={setPlan}><SelectTrigger data-testid="super-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="trial">Free trial (app locks when it ends)</SelectItem><SelectItem value="subscribed">Subscribed (only AI stops when credits run out)</SelectItem></SelectContent></Select>
          </div>
          {plan === "trial" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Trial ends</Label><Input type="date" value={ends} onChange={(e) => setEnds(e.target.value)} data-testid="super-trial-end-input" /></div>
              <div className="space-y-1.5"><Label>Or extend by days</Label><Input type="number" min="1" value={extend} onChange={(e) => setExtend(e.target.value)} placeholder="e.g. 14" data-testid="super-extend-input" /></div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>AI credit limit</Label>
            <div className="flex items-center gap-3">
              <Input type="number" min="0" step="0.5" value={unlimited ? "" : limit} disabled={unlimited} onChange={(e) => setLimit(e.target.value)} className="w-32" data-testid="super-credit-limit-input" />
              <label className="flex items-center gap-2 text-sm"><Switch checked={unlimited} onCheckedChange={setUnlimited} data-testid="super-unlimited-switch" />Unlimited</label>
            </div>
            <p className="text-[11px] text-muted-foreground">Used so far: {credits(b.credits_used)} AI credits. {CREDIT_HELP}</p>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={reset} onCheckedChange={setReset} data-testid="super-reset-used-switch" />Reset AI credits used to 0</label>
          <Button className="w-full" onClick={save} disabled={busy} data-testid="super-save-button">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteSchoolDialog({ school, onClose, onDeleted }) {
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.post(`/super/schools/${school.id}/delete`, { school_name: confirmation });
      toast.success(`${school.name} and its data were deleted`);
      onDeleted();
      onClose();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return <AlertDialog open onOpenChange={(open) => !open && onClose()}><AlertDialogContent data-testid="super-delete-school-dialog"><AlertDialogHeader><AlertDialogTitle>Delete {school.name} permanently?</AlertDialogTitle><AlertDialogDescription>All users, practicals, timetables, uploaded resources, bookings and AI credit history for this school will be permanently deleted.</AlertDialogDescription></AlertDialogHeader><div className="space-y-2"><Label htmlFor="super-delete-confirmation">Type the school name to confirm</Label><Input id="super-delete-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} data-testid="super-delete-school-name-input" /></div><AlertDialogFooter><AlertDialogCancel data-testid="super-delete-school-cancel">Cancel</AlertDialogCancel><AlertDialogAction disabled={busy || confirmation !== school.name} onClick={remove} data-testid="super-delete-school-confirm">{busy ? "Deleting…" : "Delete school"}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>;
}

export default function SuperAdminPage() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get("/super/schools").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);
  const shown = useMemo(() => (rows || []).filter((s) => `${s.name} ${s.code} ${s.admins.map((a) => a.email).join(" ")}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const stats = useMemo(() => ({ trial: (rows || []).filter((s) => s.billing.plan === "trial" && !s.billing.locked).length, ended: (rows || []).filter((s) => s.billing.locked).length, subs: (rows || []).filter((s) => s.billing.plan === "subscribed").length }), [rows]);
  return (
    <Page>
      <PageHeader title="Owner panel" subtitle="Every school on Ask Jonny, their trial and AI credit usage. Only you can see this page." />
      <SuperPrivacyPolicyPanel />
      <SuperFaqPanel />
      <SuperLoginScreenPanel />
      <SuperPageGuidancePanel />
      <div className="flex flex-wrap gap-3 mb-4 text-sm" data-testid="super-stats">
        <span className="rounded-lg border px-3 py-1.5">{rows?.length ?? "…"} schools</span>
        <span className="rounded-lg border px-3 py-1.5">{stats.trial} on trial</span>
        <span className="rounded-lg border px-3 py-1.5">{stats.ended} trial ended</span>
        <span className="rounded-lg border px-3 py-1.5">{stats.subs} subscribed</span>
      </div>
      <div className="relative max-w-sm mb-4"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9" placeholder="Search school, code or admin email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="super-search-input" /></div>
      {rows && !shown.length ? <Empty title="No schools found" /> : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="super-schools-table">
            <thead className="text-xs text-muted-foreground text-left"><tr className="border-b">
              <th className="p-3">School</th><th className="p-3">Admins</th><th className="p-3">Status</th><th className="p-3">AI credits</th><th className="p-3">Last 30 days</th><th className="p-3">Registered</th><th className="p-3" />
            </tr></thead>
            <tbody>{shown.map((s) => (
              <tr key={s.id} className="border-b last:border-0" data-testid={`super-school-row-${s.id}`}>
                <td className="p-3"><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.code} · {s.users} user{s.users === 1 ? "" : "s"}{s.is_demo && " · demo"}</div></td>
                <td className="p-3 text-xs">{s.admins.slice(0, 2).map((a) => <div key={a.email}>{a.email}</div>)}{s.admins.length > 2 && <div className="text-muted-foreground">+{s.admins.length - 2} more</div>}</td>
                <td className="p-3"><Status b={s.billing} />{s.billing.trial_ends_at && <div className="text-[11px] text-muted-foreground mt-1">ends {fmtDate(s.billing.trial_ends_at.slice(0, 10))}</div>}</td>
                <td className="p-3"><Credits b={s.billing} /></td>
                <td className="p-3 text-xs">{credits(s.usage_30d)} AI credits · {s.ai_actions_30d} AI actions</td>
                <td className="p-3 text-xs">{s.created_at ? fmtDate(s.created_at.slice(0, 10)) : "—"}</td>
                <td className="p-3"><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setEdit(s)} data-testid={`super-edit-${s.id}`}><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Manage</Button>{!s.is_demo && <Button size="icon" variant="ghost" onClick={() => setDeleting(s)} title="Delete school" data-testid={`super-delete-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {edit && <EditDialog school={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {deleting && <DeleteSchoolDialog school={deleting} onClose={() => setDeleting(null)} onDeleted={load} />}
    </Page>
  );
}
